// src/Pages/News.jsx


import React, { useState } from 'react';
import { Calendar, Tag, ExternalLink, Newspaper, Sparkles, FileText, Beaker } from 'lucide-react';

// Mock data - replace with your actual posts
const initialPosts = [
  {
    id: '1',
    title: 'New RAG System: 40% Improvement in Retrieval Accuracy',
    content: 'We\'ve implemented a hybrid retrieval system combining dense and sparse embeddings, resulting in significantly better context retrieval for complex queries. This update includes better chunking strategies and metadata filtering.',
    type: 'update',
    date: '2024-01-15',
    image: null,
    link: 'https://example.com/rag-update',
    author: 'GnosisFT Team'
  },
  {
    id: '2',
    title: 'Research Paper: "Attention Mechanisms in Long-Context LLMs"',
    content: 'Our latest research explores how attention weights decay in contexts beyond 100k tokens. Key finding: positional information matters more than previously thought for retrieval tasks.',
    type: 'research',
    date: '2024-01-12',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop',
    link: 'https://arxiv.org/example',
    author: 'Research Team'
  },
  {
    id: '3',
    title: 'Platform Update: New Embedding Models Available',
    content: 'Three new embedding models are now available in production: ada-003, gemini-embedding-004, and our custom fine-tuned model. Benchmarks show 15% cost reduction with similar performance.',
    type: 'announcement',
    date: '2024-01-10',
    image: null,
    link: null,
    author: 'Engineering'
  }
];

const PostTypeConfig = {
  update: { 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Sparkles,
    label: 'Update'
  },
  research: { 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: Beaker,
    label: 'Research'
  },
  announcement: { 
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: Newspaper,
    label: 'Announcement'
  },
  paper: { 
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: FileText,
    label: 'Paper'
  }
};

const CreatePost = ({ onPost }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState('update');
  const [link, setLink] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;

    const post = {
      id: crypto.randomUUID(),
      title: title.trim(),
      content: content.trim(),
      type,
      date: new Date().toISOString().split('T')[0],
      image: null,
      link: link.trim() || null,
      author: 'GnosisFT Team'
    };

    onPost(post);
    setTitle('');
    setContent('');
    setLink('');
    setIsExpanded(false);
  };

return (
    // Reduced padding from p-6 to p-4
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Newspaper className="w-5 h-5 text-blue-600" />
        <h3 className="text-base font-bold text-gray-900">Create News Post</h3>
      </div>

      <div className="space-y-3">
        <div>
          <input
            type="text"
            placeholder="Post title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onFocus={() => setIsExpanded(true)}
            // Reduced padding and font size (text-lg -> text-sm)
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-semibold"
          />
        </div>

        {isExpanded && (
          <>
            <div>
              <textarea
                placeholder="What's the news? Share updates, research, or announcements..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={3}
                // Reduced padding
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
              />
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="update">Update</option>
                  <option value="research">Research</option>
                  <option value="announcement">Announcement</option>
                  <option value="paper">Paper</option>
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Link (optional)</label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => {
                  setIsExpanded(false);
                  setTitle('');
                  setContent('');
                  setLink('');
                }}
                className="px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!title.trim() || !content.trim()}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Publish
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const NewsPost = ({ post }) => {
  const config = PostTypeConfig[post.type] || PostTypeConfig.update;
  const Icon = config.icon;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

return (
    <article className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {post.image && (
        // Reduced image height from h-64 to h-48
        <div className="w-full h-48 overflow-hidden">
          <img 
            src={post.image} 
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Reduced padding from p-6 to p-5 */}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
            <Icon className="w-3 h-3" />
            {config.label}
          </span>
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <Calendar className="w-3 h-3" />
            {formatDate(post.date)}
          </span>
        </div>

        {/* Reduced text size from text-2xl to text-xl */}
        <h2 className="text-xl font-bold text-gray-900 mb-2 hover:text-blue-600 transition-colors cursor-pointer">
          {post.title}
        </h2>

        <p className="text-sm text-gray-600 leading-relaxed mb-4">
          {post.content}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            By {post.author}
          </span>

          {post.link && (
            <a
              href={post.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors text-sm font-medium"
            >
              Read more
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export default function News() {
  const [posts, setPosts] = useState(initialPosts);
  const [filter, setFilter] = useState('all');

  const handleNewPost = (newPost) => {
    setPosts([newPost, ...posts]);
  };

  const filteredPosts = filter === 'all' 
    ? posts 
    : posts.filter(post => post.type === filter);

  return (
    <div className="min-h-screen overflow-y-auto">
      {/* CHANGED: 
        1. Reduced max-w-5xl to max-w-4xl (narrower content).
        2. Changed py-12 to pt-8 pb-24 (adds huge gap at bottom).
      */}
      <div className="max-w-4xl mx-auto px-4 pt-8 pb-32">
        
        {/* Header: Reduced bottom margin and font sizes */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Latest News & Updates
          </h1>
          <p className="text-base text-gray-600">
            Stay updated with the latest developments, research, and announcements from GnosisFT
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              filter === 'all'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            All Posts
          </button>
          {Object.entries(PostTypeConfig).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  filter === key
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <Icon className="w-3 h-3" />
                {config.label}
              </button>
            );
          })}
        </div>

        {/* Create Post */}
        <CreatePost onPost={handleNewPost} />

        {/* Posts Grid */}
        <div className="space-y-6">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-100">
              <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No posts found</p>
            </div>
          ) : (
            filteredPosts.map(post => (
              <NewsPost key={post.id} post={post} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}